import { Stack, TabPanel, TabPanelProps, Td, Th, Tr } from '@chakra-ui/react';
import {
  useFetchClientRolesQuery,
  usePostRoleMutation,
  usePutRoleMutation,
} from 'api';
import { useAuth, usePagination } from 'core';
import { AppButton, CurrencyLabel } from 'features/components';
import { AppTable } from 'features/table';
import { useMemo, useState } from 'react';
import { CreateClientRoleFormDrawer } from './CreateClientRoleFormDrawer';
import { EditClientRoleFormDrawer } from './EditClientRoleFormDrawer';
import { PostRoleRequest } from 'api/bindings/PostRoleRequest';
import { PutRoleRequest } from 'api/bindings/PutRoleRequest';
import { RoleResponse } from 'api/bindings/RoleResponse';

export interface ClientRolesTabPanelProps extends TabPanelProps {}

export const ClientRolesTabPanel = (props: ClientRolesTabPanelProps) => {
  const { ...others } = props;
  const auth = useAuth();
  const [page, setPagination] = usePagination();
  const { data: roles, isLoading } = useFetchClientRolesQuery({
    clientId: auth?.user?.clientId ?? "0",
  });
  const [postRole] = usePostRoleMutation();
  const [putRole] = usePutRoleMutation();

  const [editingRole, setEditingRole] = useState<RoleResponse>();
  const [isCreating, setIsCreating] = useState(false);

  const item_slice = useMemo(() => {
    let start = Math.floor(page.page - 1 / page.size);
    let end = Math.floor(page.page - 1 / page.size + page.size);

    return [start, end];
  }, [page.page, page.size]);

  const handlePostSubmit = async (role: PostRoleRequest) => {
    await postRole(role);
    setIsCreating(false);
  };

  const handlePutSubmit = async (role: PutRoleRequest) => {
    await putRole(role);
    setEditingRole(undefined);
  };

  let body = (
    <>
      <Tr>
        <Td colSpan={7}>Loading...</Td>
      </Tr>
    </>
  );

  if (!isLoading) {
    body = (
      <>
        {roles?.items.slice(item_slice[0], item_slice[1]).map((role, i) => (
          <Tr key={i}>
            <Td>{role.name}</Td>
            <Td>{role.users.length}</Td>
            <Td>
              <CurrencyLabel value={role.defaultAfeApprovalAuthority} />
            </Td>
            <Td>
              <CurrencyLabel
                value={role.defaultPurchaseOrderApprovalAuthority}
              />
            </Td>
            <Td>
              <CurrencyLabel value={role.defaultWorkOrderApprovalAuthority} />
            </Td>
            <Td>
              <CurrencyLabel value={role.defaultInvoiceApprovalAuthority} />
            </Td>
            <Td>
              <AppButton onClick={() => setEditingRole(role)}>Edit</AppButton>
            </Td>
          </Tr>
        ))}
      </>
    );
  }

  return (
    <TabPanel {...others}>
      {isCreating && (
        <CreateClientRoleFormDrawer
          isOpen={true}
          onClose={() => setIsCreating(false)}
          onSubmit={handlePostSubmit}
        />
      )}
      {editingRole && (
        <EditClientRoleFormDrawer
          isOpen={true}
          role={editingRole}
          onClose={() => setEditingRole(undefined)}
          onSubmit={handlePutSubmit}
        />
      )}
      <Stack>
        <AppButton onClick={() => setIsCreating(true)}>
          Create New Role
        </AppButton>
        <AppTable
          title="Search Roles"
          body={body}
          pagination={{
            ...page,
            itemCount: Number(roles?.itemCount) ?? 0,
            pageCount: Number(roles?.pageCount) ?? 0,
          }}
          onPaginationChange={setPagination}
          header={
            <>
              <Tr>
                <Th>Name</Th>
                <Th>Users</Th>
                <Th>AFE Authority</Th>
                <Th>Purchase Order Authority</Th>
                <Th>Work Order Authority</Th>
                <Th>Invoice Authority</Th>
                <Th></Th>
              </Tr>
            </>
          }
        />
      </Stack>
    </TabPanel>
  );
};
